import { useSelector } from "@tanstack/react-store";
import {
    DateRange,
    LoadingWrapper,
    VaporPageStickable,
} from "@vapor/react-contrib-tax-compliance";
import { Box, Stack, useMediaQuery } from "@vapor/react-material";
import dayjs from "dayjs";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { APP_VERSION, VITE_APP_ENV } from "../../../config";
import { fulfilmentSearchFiltersStoreSlice } from "../../../store/fulfilmentSearchFiltersStoreSlice";
import { store } from "../../../store/store";

import type { Theme } from "@mui/system";
import type { FulfilmentDto } from "../../../core/api/use-get-fulfilments/FulfilmentDto";
import { useGetFulfilments } from "../../../core/api/use-get-fulfilments/useGetFulfilments";
import { useGetLinkableFulfilments } from "../../../core/api/use-get-linkable-fulfilments/useGetLinkableFulfilments";
import { useWarningNotification } from "../../../core/hooks/useWarningNotification";
import { sessionStoreSlice } from "../../../store/sessionStoreSlice";
import { mergeFulfilments } from "../../../utils/fulfilmentUtils";
import { AddOrEditCustomFulfilment } from "./components/add-or-edit-custom-fulfilment/AddOrEditCustomFulfilment";
import { FulfilmentAssociation } from "./components/fulfilment-linking/FulfilmentLinking";
import { FulfilmentListHeader } from "./components/fulfilment-list-header/FulfilmentListHeader";
import { FulfilmentListUnlinked } from "./components/fulfilment-list-unlinked/FulfilmentListUnlinked";
import { FulfilmentList } from "./components/fulfilment-list/FulfilmentList";
import { FulfilmentTaxPayerList } from "./components/fulfilment-tax-payer-list/FulfilmentTaxPayerList";

interface FulfilmentAssociationAction {
    type: "SET" | "UNSET";
    payload?: FulfilmentDto;
}

interface FulfilmentAssociationState {
    fulfilment?: FulfilmentDto;
}

interface CustomFulfilmentAction {
    type: "ADD" | "EDIT" | "CLEAR";
    payload?: FulfilmentDto;
}

interface CustomFulfilmentState {
    status: "add" | "edit" | "";
    fulfilment?: FulfilmentDto;
}

function fulfilmentAssociationReducer(
    _state: FulfilmentAssociationState,
    action: FulfilmentAssociationAction,
): FulfilmentAssociationState {
    switch (action.type) {
        case "SET":
            return { fulfilment: action.payload };
        case "UNSET":
            return { fulfilment: undefined };
    }
}

function customFulfilmentReducer(
    state: CustomFulfilmentState,
    action: CustomFulfilmentAction,
): CustomFulfilmentState {
    switch (action.type) {
        case "ADD":
            return { status: "add" };
        case "EDIT":
            return { status: "edit", fulfilment: action.payload };
        case "CLEAR":
            return { status: "" };
        default:
            return state;
    }
}

const expiringDays = 7;

export const Home = () => {
    // states
    const { showTextMessage: showWarningMessage } = useWarningNotification();
    const { t } = useTranslation();
    const selectedFulfilment = useSelector(
        store,
        (state) => state.session.selectedFulfilment,
    );

    const isXlScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.up(theme.breakpoints.values.xl),
    );
    const devOrTestEnv =
        VITE_APP_ENV === "development" || VITE_APP_ENV === "test";

    // states->fulfilments
    const fulfilmentFilterSearch = useSelector(
        store,
        (state) => state.fulfilmentSearchFilters,
    );

    // states->fulfilment to link
    const [fulfilmentAssociation, dispatchFulfilmentAssociation] = useReducer(
        fulfilmentAssociationReducer,
        { fulfilment: undefined },
    );

    // states -> header right width used to create a fake header left spacer in order to center title and subtitle
    const headerRightRef = useRef<HTMLDivElement>(null);
    const [headerRightWidth, setHeaderRightWidth] = useState(0);

    // states->custom fulfilment
    const [customFulfilment, dispatchCustomFulfilment] = useReducer(
        customFulfilmentReducer,
        {
            status: "",
            fulfilment: undefined,
        },
    );

    // use cases — declarative TanStack Query hooks
    const { data: getFulfilmentsData, isFetching: getFulfilmentsLoading } =
        useGetFulfilments();

    const {
        data: getCustomLinkableFulfilmentsData,
        isFetching: getCustomLinkableFulfilmentsLoading,
    } = useGetLinkableFulfilments(true);

    // treat missing linkable data as empty rather than blocking the whole merge —
    // prevents mergedFulfilments from becoming undefined when getCustomLinkableFulfilmentsData
    // is temporarily undefined during workspace initialisation or query key changes
    const mergedFulfilments = useMemo(
        () => ({
            fulfilments: mergeFulfilments(
                getFulfilmentsData.fulfilments,
                getCustomLinkableFulfilmentsData?.fulfilments ?? {},
            ),
            taxPayers: getFulfilmentsData.taxPayers,
        }),
        [getFulfilmentsData, getCustomLinkableFulfilmentsData],
    );

    const drawerContent = useMemo(() => {
        if (selectedFulfilment && selectedFulfilment.taxPayerCounters) {
            return {
                sxContent: { padding: 0 },
                content: (
                    <FulfilmentTaxPayerList
                        selectedFulfilment={selectedFulfilment}
                        taxPayerActivityCounters={
                            selectedFulfilment.taxPayerCounters
                        }
                    />
                ),
                headerDescription: selectedFulfilment.title,
                headerTitle: selectedFulfilment.description,
                onClickCloseButton: () =>
                    sessionStoreSlice.updateValues({
                        selectedFulfilment: undefined,
                    }),
                open: !!selectedFulfilment,
                width: isXlScreen ? "40%" : "60%",
            };
        } else if (customFulfilment.status === "add") {
            return {
                content: (
                    <AddOrEditCustomFulfilment
                        mode="add"
                        onAddOrEditCustomFulfilment={() => {
                            dispatchCustomFulfilment({ type: "CLEAR" });
                        }}
                        onClickCancel={() => {
                            dispatchCustomFulfilment({ type: "CLEAR" });
                        }}
                    />
                ),
                headerTitle: t(
                    "views.home.drawer.add_or_edit_custom_fulfilment.header.title.add",
                ),
                onClickCloseButton: () => {
                    dispatchCustomFulfilment({ type: "CLEAR" });
                },
                open: true,
                sxContent: { padding: 0 },
                width: isXlScreen ? "30%" : "40%",
            };
        } else if (customFulfilment.status === "edit") {
            return {
                content: (
                    <AddOrEditCustomFulfilment
                        fulfilment={customFulfilment.fulfilment}
                        mode="edit"
                        onAddOrEditCustomFulfilment={() => {
                            dispatchCustomFulfilment({ type: "CLEAR" });
                        }}
                        onClickCancel={() => {
                            dispatchCustomFulfilment({ type: "CLEAR" });
                        }}
                    />
                ),
                headerTitle: t(
                    "views.home.drawer.add_or_edit_custom_fulfilment.header.title.edit",
                ),
                onClickCloseButton: () => {
                    dispatchCustomFulfilment({ type: "CLEAR" });
                },
                open: true,
                sxContent: { padding: 0 },
                width: isXlScreen ? "30%" : "40%",
            };
        } else if (fulfilmentAssociation.fulfilment) {
            return {
                sxContent: { padding: 0 },
                content: (
                    <FulfilmentAssociation
                        levyId={fulfilmentAssociation.fulfilment.levyId}
                        year={dayjs(
                            fulfilmentAssociation.fulfilment.deadline,
                        ).year()}
                    />
                ),
                headerDescription: t(
                    "views.home.drawer.fulfilment_association.header.title",
                ),
                headerTitle: fulfilmentAssociation.fulfilment.description,
                onClickCloseButton: () => {
                    dispatchFulfilmentAssociation({ type: "UNSET" });
                },
                open: !!fulfilmentAssociation.fulfilment,
                width: isXlScreen ? "40%" : "60%",
            };
        }
    }, [
        customFulfilment.fulfilment,
        customFulfilment.status,
        fulfilmentAssociation.fulfilment,
        isXlScreen,
        selectedFulfilment,
        t,
    ]);

    useEffect(() => {
        const updateWidth = () => {
            if (headerRightRef.current) {
                setHeaderRightWidth(headerRightRef.current.offsetWidth);
            }
        };

        updateWidth();
        window.addEventListener("resize", updateWidth);

        return () => {
            window.removeEventListener("resize", updateWidth);
        };
    }, []);

    //functions
    const handleDateRangeValidation = () => {
        showWarningMessage(t("views.home.date_range.warning"));
    };

    const resolveSubtitle = () => {
        let subtitle: string = t("views.home.header.subtitle");
        if (devOrTestEnv) {
            subtitle += `${"\n"}${VITE_APP_ENV} ${APP_VERSION}`;
        }
        return subtitle;
    };

    return (
        <VaporPageStickable
            drawer={drawerContent}
            headerBottom={
                <Stack
                    alignContent="baseline"
                    direction="row"
                    justifyContent="space-between"
                    padding={2}
                    spacing={10}
                >
                    <Box flexGrow={2}>
                        <FulfilmentListHeader
                            onClickAddCustomFulfilment={() =>
                                dispatchCustomFulfilment({
                                    type: "ADD",
                                })
                            }
                        />
                    </Box>
                </Stack>
            }
            headerBottomDivider
            headerLeft={
                // fake spacer to center title and subtitle
                <Box visibility="hidden" width={headerRightWidth}></Box>
            }
            headerRight={
                <Stack
                    alignItems="center"
                    direction="row"
                    id="header-right"
                    spacing={2}
                    mr={2}
                    ref={headerRightRef}
                >
                    <DateRange
                        disableValidationAlert
                        endDate={fulfilmentFilterSearch.deadlineTo}
                        expiringDays={expiringDays}
                        handleCustomValidation={handleDateRangeValidation}
                        isValidDateRange={false}
                        onChangeDates={(startDate, endDate) => {
                            fulfilmentSearchFiltersStoreSlice.updateValues({
                                deadlineFrom: startDate,
                                deadlineTo: endDate,
                            });
                        }}
                        showPeriodSelectionMenu
                        startDate={fulfilmentFilterSearch.deadlineFrom}
                    />
                </Stack>
            }
            stickyHeader
            sxContent={{ padding: 2 }}
            subtitle={resolveSubtitle()}
            sxHeader={{ whiteSpace: "pre-line" }}
            title={t("views.home.header.title")}
        >
            <Stack direction="column" spacing={2}>
                <LoadingWrapper
                    loading={
                        getFulfilmentsLoading ||
                        getCustomLinkableFulfilmentsLoading
                    }
                    variant="skeleton-list"
                >
                    <FulfilmentList
                        fulfilments={mergedFulfilments}
                        onClickFulfilment={(fulfilment: FulfilmentDto) => {
                            sessionStoreSlice.updateValues({
                                selectedFulfilment: fulfilment,
                            });
                        }}
                        onClickEditCustomFulfilment={(
                            fulfilment: FulfilmentDto,
                        ) => {
                            dispatchCustomFulfilment({
                                type: "EDIT",
                                payload: fulfilment,
                            });
                        }}
                        onClickLinkTaxPayersToFulfilment={(fulfilment) => {
                            dispatchFulfilmentAssociation({
                                type: "SET",
                                payload: fulfilment,
                            });
                        }}
                    />
                    <FulfilmentListUnlinked
                        onClickLinkTaxPayersToFulfilment={(fulfilment) =>
                            dispatchFulfilmentAssociation({
                                type: "SET",
                                payload: fulfilment,
                            })
                        }
                    />
                </LoadingWrapper>
            </Stack>
        </VaporPageStickable>
    );
};
